// src/pages/PromotionsPage.tsx — акции: каждый N-й чек, скидки на услуги и товары, условия «купил А — скидка на Б»
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import { getRole } from "../hooks/usePermissions";
import "./PosPage.css";
import "./PromotionsPage.css";

interface Scope { types?: string[]; categoryIds?: number[]; productIds?: number[] }
interface Cfg { percent: number; scope: Scope; trigger?: Scope; everyN?: number; countMode?: "any" | "scope"; pipelineIds?: number[]; couponDays?: number }
interface Loyalty { enabled: boolean; accrualPercent: number; maxPayPercent: number; expiryDays: number }
interface Promo {
  id: number; name: string; description: string | null; active: boolean; startsAt: string | null; endsAt: string | null;
  config: Cfg; running: boolean; claims: number; prizes: number; discountSum: number; counted: number; nextIsPrize: boolean; untilPrize: number | null;
}
interface Claim { seq: number; isPrize: boolean; discountSum: number; claimedAt: string; dealId: number; number: string; amount: number }
interface Prod { id: number; nameRu: string; type: string; salePrice: number; categoryId?: number | null }
interface Cat { id: number; name: string; folderId: number }
interface Pipe { id: number; name: string }

const TYPE_RU: Record<string, string> = { service: "Услуги", product: "Товары", software: "ПО и подписки" };
const money = (v: number) => `${Number(v).toLocaleString("ru-RU")} ₸`;
const fmtDate = (s: string | null) => (s ? s.split("-").reverse().join(".") : "");
const fmtDt = (iso: string) => new Date(iso).toLocaleString("ru-RU", { timeZone: "Asia/Almaty", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const emptyScope = (): Scope => ({ types: [], categoryIds: [], productIds: [] });
const blank = () => ({
  id: 0, name: "", description: "", active: true, startsAt: "", endsAt: "",
  percent: "40", everyN: "", countMode: "any" as "any" | "scope", couponDays: "",
  scope: emptyScope(), useTrigger: false, trigger: emptyScope(), pipelineIds: [] as number[],
});
type Form = ReturnType<typeof blank>;

const TEMPLATES: { key: string; icon: string; title: string; text: string; make: () => Partial<Form> }[] = [
  { key: "nth", icon: "🎯", title: "Каждый N-й чек со скидкой", text: "Например, каждый 3-й чек: −40% на услуги по обслуживанию ПК.", make: () => ({ name: "Каждый 3-й чек: −40% на услуги", everyN: "3", percent: "40", scope: { types: ["service"], categoryIds: [], productIds: [] } }) },
  { key: "combo", icon: "🧩", title: "Купил А — скидка на Б", text: "Например, при установке ОС −50% на чистку в этом же чеке.", make: () => ({ name: "Установка ОС: −50% на чистку", percent: "50", useTrigger: true }) },
  { key: "coupon", icon: "🎟️", title: "Купон на следующую покупку", text: "Например, установили Windows — клиенту купон −50% на чистку на 30 дней. Нужен телефон клиента.", make: () => ({ name: "Установка ОС: купон −50% на чистку", percent: "50", useTrigger: true, couponDays: "30" }) },
  { key: "plain", icon: "🏷️", title: "Скидка на период", text: "Скидка на услуги, категорию или товары на определённые даты.", make: () => ({ name: "Скидка на период", percent: "10" }) },
];

/** Словами: что делает акция */
function describe(c: Cfg, prods: Map<number, Prod>, cats: Map<number, Cat>) {
  const names = (s?: Scope) => {
    const parts: string[] = [];
    (s?.types ?? []).forEach((t) => parts.push(TYPE_RU[t] ?? t));
    (s?.categoryIds ?? []).forEach((id) => parts.push(`«${cats.get(id)?.name ?? "категория " + id}»`));
    (s?.productIds ?? []).forEach((id) => parts.push(prods.get(id)?.nameRu ?? `товар ${id}`));
    return parts.join(", ") || "—";
  };
  const when = c.everyN ? `Каждый ${c.everyN}-й чек` : "Каждый подходящий чек";
  const cond = c.trigger && ((c.trigger.types ?? []).length || (c.trigger.categoryIds ?? []).length || (c.trigger.productIds ?? []).length) ? `, если в чеке есть: ${names(c.trigger)}` : "";
  if (c.couponDays) return `Если в чеке есть: ${names(c.trigger)} → клиенту купон −${c.percent}% на ${names(c.scope)} на ${c.couponDays} дн.`;
  return `${when}${cond}: −${c.percent}% на ${names(c.scope)}`;
}

function ScopePicker({ value, onChange, prods, cats, folders }: { value: Scope; onChange: (s: Scope) => void; prods: Prod[]; cats: Cat[]; folders: Map<number, string> }) {
  const [q, setQ] = useState("");
  const [cq, setCq] = useState("");
  const types = value.types ?? [];
  const cIds = value.categoryIds ?? [];
  const pIds = value.productIds ?? [];
  const toggle = <T,>(arr: T[], v: T) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const byId = useMemo(() => new Map(prods.map((p) => [p.id, p])), [prods]);
  const catById = useMemo(() => new Map(cats.map((c) => [c.id, c])), [cats]);
  const foundP = q.trim().length >= 2 ? prods.filter((p) => p.nameRu?.toLowerCase().includes(q.trim().toLowerCase()) && !pIds.includes(p.id)).slice(0, 8) : [];
  const foundC = cq.trim().length >= 1 ? cats.filter((c) => c.name?.toLowerCase().includes(cq.trim().toLowerCase()) && !cIds.includes(c.id)).slice(0, 8) : [];
  return (
    <div className="pm-scope">
      <div className="pm-sub">Типы позиций</div>
      <div className="pm-chips">
        {Object.entries(TYPE_RU).map(([t, n]) => (
          <button type="button" key={t} className={`pm-chip ${types.includes(t) ? "on" : ""}`} onClick={() => onChange({ ...value, types: toggle(types, t) })}>{n}</button>
        ))}
      </div>
      <div className="pm-sub">Категории каталога</div>
      <div className="pm-chips">
        {cIds.map((id) => <button type="button" key={id} className="pm-chip on" onClick={() => onChange({ ...value, categoryIds: cIds.filter((x) => x !== id) })}>{catById.get(id)?.name ?? id} ×</button>)}
      </div>
      <input className="ps-input" placeholder="Найти категорию…" value={cq} onChange={(e) => setCq(e.target.value)} />
      {foundC.length > 0 && <div className="pm-found">{foundC.map((c) => <button type="button" key={c.id} onClick={() => { onChange({ ...value, categoryIds: [...cIds, c.id] }); setCq(""); }}>{c.name}<small>{folders.get(c.folderId) ?? ""}</small></button>)}</div>}
      <div className="pm-sub">Отдельные товары и услуги</div>
      <div className="pm-chips">
        {pIds.map((id) => <button type="button" key={id} className="pm-chip on" onClick={() => onChange({ ...value, productIds: pIds.filter((x) => x !== id) })}>{byId.get(id)?.nameRu ?? id} ×</button>)}
      </div>
      <input className="ps-input" placeholder="Найти товар или услугу (от 2 букв)…" value={q} onChange={(e) => setQ(e.target.value)} />
      {foundP.length > 0 && <div className="pm-found">{foundP.map((p) => <button type="button" key={p.id} onClick={() => { onChange({ ...value, productIds: [...pIds, p.id] }); setQ(""); }}>{p.nameRu}<small>{TYPE_RU[p.type] ?? p.type} · {money(p.salePrice)}</small></button>)}</div>}
    </div>
  );
}

export default function PromotionsPage() {
  const role = getRole();
  const canEdit = role === "ADMIN" || role === "OFFICE_LEAD";
  const [list, setList] = useState<Promo[] | null>(null);
  const [prods, setProds] = useState<Prod[]>([]);
  const [cats, setCats] = useState<Cat[]>([]);
  const [folders, setFolders] = useState<Map<number, string>>(new Map());
  const [pipes, setPipes] = useState<Pipe[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState<number | null>(null);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [pickTpl, setPickTpl] = useState(false);
  const [loy, setLoy] = useState<{ enabled: boolean; accrualPercent: string; maxPayPercent: string; expiryDays: string } | null>(null);
  const [loySaving, setLoySaving] = useState(false);
  const [loyMsg, setLoyMsg] = useState("");
  useEffect(() => {
    api.get("/loyalty/settings").then((r) => { const s: Loyalty = r.data; setLoy({ enabled: s.enabled, accrualPercent: String(s.accrualPercent), maxPayPercent: String(s.maxPayPercent), expiryDays: String(s.expiryDays) }); }).catch(() => undefined);
  }, []);
  const saveLoy = async () => {
    if (!loy) return;
    setLoySaving(true); setLoyMsg("");
    try {
      await api.put("/loyalty/settings", { enabled: loy.enabled, accrualPercent: Number(loy.accrualPercent), maxPayPercent: Number(loy.maxPayPercent), expiryDays: Number(loy.expiryDays) });
      setLoyMsg("Сохранено");
    } catch (e: any) { const m = e?.response?.data?.message; setLoyMsg(Array.isArray(m) ? m.join(", ") : m || "Не удалось сохранить"); }
    finally { setLoySaving(false); }
  };

  const load = useCallback(() => { api.get("/promotions").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProds((r.data ?? []).map((p: any) => ({ id: p.id, nameRu: p.nameRu, type: p.type, salePrice: Number(p.salePrice), categoryId: p.category?.id ?? p.categoryId ?? null })))).catch(() => undefined);
    api.get("/products/categories").then((r) => setCats((r.data ?? []).map((c: any) => ({ id: c.id, name: c.name, folderId: c.folderId })))).catch(() => undefined);
    api.get("/products/folders").then((r) => setFolders(new Map((r.data ?? []).map((f: any) => [f.id, f.name])))).catch(() => undefined);
    api.get("/deals/pipelines").then((r) => setPipes((r.data ?? []).map((p: any) => ({ id: p.id, name: p.name })))).catch(() => undefined);
  }, [load]);

  const prodMap = useMemo(() => new Map(prods.map((p) => [p.id, p])), [prods]);
  const catMap = useMemo(() => new Map(cats.map((c) => [c.id, c])), [cats]);

  const startNew = (patch: Partial<Form>) => { setErr(""); setPickTpl(false); setForm({ ...blank(), ...patch }); };
  const edit = (p: Promo) => {
    setErr("");
    const c = p.config;
    const hasTr = !!c.trigger && ((c.trigger.types ?? []).length + (c.trigger.categoryIds ?? []).length + (c.trigger.productIds ?? []).length) > 0;
    setForm({
      id: p.id, name: p.name, description: p.description ?? "", active: p.active, startsAt: p.startsAt ?? "", endsAt: p.endsAt ?? "",
      percent: String(c.percent), everyN: c.everyN ? String(c.everyN) : "", countMode: c.countMode ?? "any", couponDays: c.couponDays ? String(c.couponDays) : "",
      scope: { types: c.scope?.types ?? [], categoryIds: c.scope?.categoryIds ?? [], productIds: c.scope?.productIds ?? [] },
      useTrigger: hasTr, trigger: hasTr ? { types: c.trigger?.types ?? [], categoryIds: c.trigger?.categoryIds ?? [], productIds: c.trigger?.productIds ?? [] } : emptyScope(),
      pipelineIds: c.pipelineIds ?? [],
    });
  };

  const save = async () => {
    if (!form) return;
    setSaving(true); setErr("");
    try {
      const body = {
        name: form.name, description: form.description, active: form.active,
        startsAt: form.startsAt || null, endsAt: form.endsAt || null,
        config: {
          percent: Number(form.percent), scope: form.scope,
          ...(form.useTrigger || form.couponDays ? { trigger: form.trigger } : {}),
          ...(form.couponDays ? { couponDays: Number(form.couponDays) } : {}),
          ...(form.everyN && !form.couponDays ? { everyN: Number(form.everyN), countMode: form.countMode } : {}),
          ...(form.pipelineIds.length ? { pipelineIds: form.pipelineIds } : {}),
        },
      };
      if (form.id) await api.put(`/promotions/${form.id}`, body); else await api.post("/promotions", body);
      setForm(null); load();
    } catch (e: any) {
      const m = e?.response?.data?.message;
      setErr(Array.isArray(m) ? m.join("\n") : m || "Не удалось сохранить");
    } finally { setSaving(false); }
  };

  const toggleActive = async (p: Promo) => { await api.post(`/promotions/${p.id}/active`, { active: !p.active }); load(); };
  const remove = async (p: Promo) => {
    const msg = p.claims > 0 ? `У акции «${p.name}» уже есть чеки. Она будет отключена и останется в истории. Продолжить?` : `Удалить акцию «${p.name}»?`;
    if (!window.confirm(msg)) return;
    await api.delete(`/promotions/${p.id}`); load();
  };
  const showClaims = async (p: Promo) => {
    if (open === p.id) { setOpen(null); return; }
    setOpen(p.id); setClaims([]);
    api.get(`/promotions/${p.id}/claims`).then((r) => setClaims(r.data)).catch(() => setClaims([]));
  };

  const state = (p: Promo) => {
    if (!p.active) return { cls: "off", t: "Выключена" };
    const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Almaty" });
    if (p.startsAt && p.startsAt > today) return { cls: "wait", t: `Начнётся ${fmtDate(p.startsAt)}` };
    if (p.endsAt && p.endsAt < today) return { cls: "off", t: "Завершена" };
    return { cls: "on", t: "Идёт" };
  };

  return (
    <div className="ps-page pm-page">
      <div className="ps-top pm-top">
        <h2>🎁 Акции</h2>
        {canEdit && <button className="ps-btn primary" onClick={() => setPickTpl(true)}>+ Новая акция</button>}
      </div>
      <div className="pm-note">
        Акции работают в кассе, в сделках офиса и по договорам. HalykMarket и Kaspi в акциях не участвуют. Скидка считается по каждой позиции, акции не суммируются: берётся наибольшая.
        Номер чека для «каждого N-го» выдаётся при <b>первой оплате</b>, пустые сделки (например, созданные по звонку) в счётчик не попадают.
      </div>

      {loy && (
        <div className="ps-card pm-loy">
          <div className="pm-head">
            <div className="pm-title">🪙 Бонусная программа</div>
            <span className={`pm-badge ${loy.enabled ? "on" : "off"}`}>{loy.enabled ? "Включена" : "Выключена"}</span>
          </div>
          <div className="pm-desc">
            С каждой оплаченной покупки клиент (по номеру телефона) получает бонусы и тратит их на любые товары и услуги в следующих покупках, но не больше заданной части чека.
            Бонус = 1 ₸. Списание уменьшает цену, фискальный чек пробивается только на деньги.
          </div>
          <div className="pm-loy-grid">
            <div className="pm-fld"><span>Начислять, % от чека</span><input className="ps-input" inputMode="decimal" disabled={!canEdit} value={loy.accrualPercent} onChange={(e) => setLoy({ ...loy, accrualPercent: e.target.value.replace(/[^\d.,]/g, "").replace(",", ".") })} /></div>
            <div className="pm-fld"><span>Оплата бонусами, макс. % чека</span><input className="ps-input" inputMode="numeric" disabled={!canEdit} value={loy.maxPayPercent} onChange={(e) => setLoy({ ...loy, maxPayPercent: e.target.value.replace(/\D/g, "").slice(0, 3) })} /></div>
            <div className="pm-fld"><span>Срок жизни бонусов, дней</span><input className="ps-input" inputMode="numeric" disabled={!canEdit} value={loy.expiryDays} onChange={(e) => setLoy({ ...loy, expiryDays: e.target.value.replace(/\D/g, "").slice(0, 4) })} /></div>
          </div>
          {canEdit && (
            <div className="pm-actions">
              <button className="ps-btn small" onClick={() => setLoy({ ...loy, enabled: !loy.enabled })}>{loy.enabled ? "Выключить" : "Включить"}</button>
              <button className="ps-btn small primary" disabled={loySaving} onClick={saveLoy}>{loySaving ? "Сохраняю…" : "Сохранить настройки"}</button>
              {loyMsg && <span className="pm-meta" style={{ alignSelf: "center" }}>{loyMsg}</span>}
            </div>
          )}
          <div className="pm-meta">Нажмите «Включить» или «Выключить», затем «Сохранить настройки». Бонусы начисляются в кассе и в сделках офиса (HalykMarket и Kaspi не участвуют).</div>
        </div>
      )}

      {list === null && <div className="ps-muted" style={{ padding: 20 }}>Загрузка…</div>}
      {list && list.length === 0 && <div className="ps-card ps-empty"><b>🎁</b>Акций пока нет.{canEdit ? " Нажмите «Новая акция» и выберите шаблон." : ""}</div>}

      <div className="pm-list">
        {(list ?? []).map((p) => {
          const st = state(p);
          return (
            <div key={p.id} className={`ps-card pm-card ${st.cls}`}>
              <div className="pm-head">
                <div className="pm-title">{p.name}</div>
                <span className={`pm-badge ${st.cls}`}>{st.t}</span>
              </div>
              <div className="pm-desc">{describe(p.config, prodMap, catMap)}</div>
              {(p.startsAt || p.endsAt) && <div className="pm-meta">Период: {p.startsAt ? `с ${fmtDate(p.startsAt)}` : "с любой даты"} {p.endsAt ? `по ${fmtDate(p.endsAt)}` : "без даты окончания"}</div>}
              {p.description && <div className="pm-meta">{p.description}</div>}
              <div className="pm-stats">
                <div><b>{p.claims}</b><span>чеков в акции</span></div>
                <div><b>{p.prizes}</b><span>со скидкой</span></div>
                <div><b>{money(p.discountSum)}</b><span>скидок выдано</span></div>
                {p.config.everyN ? <div className={p.nextIsPrize ? "pm-next" : ""}><b>{p.nextIsPrize ? "Следующий" : `ещё ${p.untilPrize}`}</b><span>{p.nextIsPrize ? "чек призовой 🎁" : "до призового чека"}</span></div> : null}
              </div>
              <div className="pm-actions">
                <button className="ps-btn small" onClick={() => showClaims(p)}>{open === p.id ? "Скрыть чеки" : "Чеки по акции"}</button>
                {canEdit && <button className="ps-btn small" onClick={() => edit(p)}>Изменить</button>}
                {canEdit && <button className="ps-btn small" onClick={() => toggleActive(p)}>{p.active ? "Выключить" : "Включить"}</button>}
                {canEdit && <button className="ps-btn small ghost pm-del" onClick={() => remove(p)}>Удалить</button>}
              </div>
              {open === p.id && (
                <div className="pm-claims">
                  {claims.length === 0 ? <div className="ps-muted">Чеков пока нет</div> : (
                    <div className="pm-table">
                      {claims.map((c) => (
                        <div key={c.dealId} className="pm-row">
                          <span>№{c.seq}</span><span>{c.number}</span><span>{fmtDt(c.claimedAt)}</span>
                          <span>{c.isPrize ? <b className="pm-prize">🎁 {money(c.discountSum)}</b> : <span className="ps-muted">без скидки</span>}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {pickTpl && (
        <div className="ps-overlay" onClick={() => setPickTpl(false)}>
          <div className="ps-card pm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="pm-mh">Какую акцию создаём?</h3>
            <div className="pm-tpls">
              {TEMPLATES.map((t) => (
                <button key={t.key} className="pm-tpl" onClick={() => startNew(t.make())}>
                  <i>{t.icon}</i><b>{t.title}</b><span>{t.text}</span>
                </button>
              ))}
            </div>
            <div className="pm-foot"><button className="ps-btn" onClick={() => setPickTpl(false)}>Отмена</button></div>
          </div>
        </div>
      )}

      {form && (
        <div className="ps-overlay" onClick={() => !saving && setForm(null)}>
          <div className="ps-card pm-modal pm-form" onClick={(e) => e.stopPropagation()}>
            <h3 className="pm-mh">{form.id ? "Изменить акцию" : "Новая акция"}</h3>
            <div className="pm-body">
              <div className="pm-fld"><span>Название</span><input className="ps-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Например: Открытие офиса" /></div>
              <div className="pm-switch" onClick={() => setForm({ ...form, couponDays: form.couponDays ? "" : "30", useTrigger: form.couponDays ? form.useTrigger : true, everyN: form.couponDays ? form.everyN : "" })}>
                <input type="checkbox" readOnly checked={!!form.couponDays} /> <span>Выдать купон на следующую покупку (вместо скидки в этом чеке)</span>
              </div>
              <div className="pm-two">
                <div className="pm-fld"><span>{form.couponDays ? "Скидка по купону, %" : "Скидка, %"}</span><input className="ps-input" inputMode="numeric" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value.replace(/\D/g, "").slice(0, 3) })} /></div>
                {form.couponDays
                  ? <div className="pm-fld"><span>Срок купона, дней</span><input className="ps-input" inputMode="numeric" value={form.couponDays} onChange={(e) => setForm({ ...form, couponDays: e.target.value.replace(/\D/g, "").slice(0, 3) })} /></div>
                  : <div className="pm-fld"><span>Каждый N-й чек (пусто — в каждом)</span><input className="ps-input" inputMode="numeric" placeholder="например, 3" value={form.everyN} onChange={(e) => setForm({ ...form, everyN: e.target.value.replace(/\D/g, "").slice(0, 3) })} /></div>}
              </div>
              {form.couponDays && <small className="pm-hint">Купон выдаётся клиенту при первой оплате чека, где есть позиция из условия ниже, и действует в следующих покупках. Без телефона клиента купон не выдаётся. Ниже «Скидка действует на» — на что купон даёт скидку.</small>}
              {form.everyN && !form.couponDays && (
                <div className="pm-fld">
                  <span>Что считать чеком для счётчика</span>
                  <div className="pm-chips">
                    <button type="button" className={`pm-chip ${form.countMode === "any" ? "on" : ""}`} onClick={() => setForm({ ...form, countMode: "any" })}>Любой оплаченный чек</button>
                    <button type="button" className={`pm-chip ${form.countMode === "scope" ? "on" : ""}`} onClick={() => setForm({ ...form, countMode: "scope" })}>Только чек, где есть позиции со скидкой</button>
                  </div>
                  <small className="pm-hint">Счёт идёт за всё время акции, а не по дням: если вчера было 5 чеков, завтрашний первый — 6-й. Если у призового чека нет позиций со скидкой, приз сгорает.</small>
                </div>
              )}
              <div className="pm-two">
                <div className="pm-fld"><span>Начало (пусто — сразу)</span><input className="ps-input" type="date" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} /></div>
                <div className="pm-fld"><span>Окончание (включительно)</span><input className="ps-input" type="date" value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} /></div>
              </div>
              <div className="pm-fld">
                <span>Скидка действует на</span>
                <ScopePicker value={form.scope} onChange={(s) => setForm({ ...form, scope: s })} prods={prods} cats={cats} folders={folders} />
              </div>
              <div className="pm-fld">
                <div className="pm-switch" onClick={() => !form.couponDays && setForm({ ...form, useTrigger: !form.useTrigger })}>
                  <input type="checkbox" readOnly checked={form.useTrigger || !!form.couponDays} /> <span>{form.couponDays ? "Купон выдаётся, если в чеке есть позиция" : "Только если в чеке есть определённая позиция"}</span>
                </div>
                {(form.useTrigger || !!form.couponDays) && (
                  <>
                    <small className="pm-hint">Например: «Установка Windows» в этом же чеке даёт скидку на чистку.</small>
                    <ScopePicker value={form.trigger} onChange={(s) => setForm({ ...form, trigger: s })} prods={prods} cats={cats} folders={folders} />
                  </>
                )}
              </div>
              <div className="pm-fld">
                <span>Воронки (пусто — все, кроме HalykMarket)</span>
                <div className="pm-chips">
                  {pipes.map((p) => (
                    <button type="button" key={p.id} className={`pm-chip ${form.pipelineIds.includes(p.id) ? "on" : ""}`}
                      onClick={() => setForm({ ...form, pipelineIds: form.pipelineIds.includes(p.id) ? form.pipelineIds.filter((x) => x !== p.id) : [...form.pipelineIds, p.id] })}>{p.name}</button>
                  ))}
                </div>
              </div>
              <div className="pm-fld"><span>Заметка для сотрудников</span><input className="ps-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="pm-switch" onClick={() => setForm({ ...form, active: !form.active })}>
                <input type="checkbox" readOnly checked={form.active} /> <span>Акция включена</span>
              </div>
              {err && <div className="ps-warn" style={{ whiteSpace: "pre-line" }}>{err}</div>}
            </div>
            <div className="pm-foot">
              <button className="ps-btn" disabled={saving} onClick={() => setForm(null)}>Отмена</button>
              <button className="ps-btn primary" disabled={saving} onClick={save}>{saving ? "Сохраняю…" : "Сохранить"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
